'use client'

import { forwardRef, useEffect, useRef, useState } from 'react'
import animationDataDark from '@/public/animated/losi-black-full.json'
import animationDataLight from '@/public/animated/losi-white-full.json'
import { useTheme } from 'next-themes'

import { cn } from '@/lib/utils'

interface LosiAnimatedProps extends React.HTMLAttributes<HTMLDivElement> {
  className?: string
  size: number | string
  waitForComponent?: React.ReactNode // New prop to indicate we should wait
  isComponentLoaded?: boolean // New prop to trigger animation
}

export const LosiAnimated = forwardRef<any, LosiAnimatedProps>(
  ({ className = '', size = '20', waitForComponent, isComponentLoaded = true, ...props }, ref) => {
    const lottieRef = useRef<any>()
    const { theme, resolvedTheme } = useTheme()
    const [mounted, setMounted] = useState(false)
    const [PlayerComponent, setPlayerComponent] = useState<any>(null)

    useEffect(() => {
      setMounted(true)
    }, [])

    useEffect(() => {
      let active = true

      void import('@lottiefiles/react-lottie-player')
        .then((module) => {
          if (active) {
            setPlayerComponent(() => module.Player)
          }
        })
        .catch(() => {
          if (active) {
            setPlayerComponent(null)
          }
        })

      return () => {
        active = false
      }
    }, [])

    const effectiveTheme = mounted ? resolvedTheme || theme : null

    return (
      <div
        className={cn(`w-${size}px h-${size}px`, className)}
        {...props}
        style={{
          height: `${size}px`,
          width: `${size}px`
        }}
      >
        {mounted && PlayerComponent ? (
          <PlayerComponent
            ref={ref || lottieRef}
            loop={true}
            autoplay={true}
            src={effectiveTheme === 'dark' ? animationDataDark : animationDataLight}
            className={cn(`size-full`, className)}
            speed={1}
            keepLastFrame={true}
            renderer="svg"
            {...props}
          />
        ) : null}
      </div>
    )
  }
)

LosiAnimated.displayName = 'LosiAnimated'
